import React, { useState, useEffect } from 'react'
import { AnalyticsMetricType } from 'src/lib/types/analytics'
import { RewardAnalyticsWidgetType } from 'src/lib/types/rewards'
import GenericMetricData from '../../common/analytics-components/GenericMetricData'
import useSWR, { mutate } from 'swr'

import { fetcher, staticOptions } from 'src/lib/core/api-client'
import { parseRewardsAnalyticsWidgetsData } from 'src/lib/utils/parsers'
import { FilterConfigType } from 'src/lib/types/filters'
import { extractDateFromFilters } from 'src/lib/utils/analyticsHelpers'
import { apiURI } from 'src/lib/utils/constants'
import ComponentLoader from 'src/components/common/loader-components/ComponentLoader'

interface RewardAnalyticsWidgetProps {
  widget: RewardAnalyticsWidgetType
  filters: FilterConfigType
}

const RewardAnalyticsWidget: React.FC<RewardAnalyticsWidgetProps> = ({ widget, filters }) => {
  const [widgetData, setWidgetData] = useState<RewardAnalyticsWidgetType>(widget)

  const getFiltersString = () => {
    const searchParameters: any = {
      start_date: extractDateFromFilters(filters, 'startDate', 'EPOCH'),
      end_date: extractDateFromFilters(filters, 'endDate', 'EPOCH')
    }
    if (Boolean(filters.comparison !== 'none')) {
      searchParameters['compare_start_date'] = extractDateFromFilters(filters, 'compareToStartDate', 'EPOCH')
      searchParameters['compare_end_date'] = extractDateFromFilters(filters, 'compareToEndDate', 'EPOCH')
    }
    return new URLSearchParams(searchParameters).toString()
  }

  const fetchURL = `/dashboard/rewards/widget/${widget.id}?${getFiltersString()}`

  const { data: widgetResponse, isValidating } = useSWR(
    [fetchURL, apiURI.CHECKOUT_NODE],
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  useEffect(() => {
    mutate([fetchURL, apiURI.CHECKOUT_NODE])
  }, [filters])

  useEffect(() => {
    if (!Boolean(widgetResponse) || Boolean(isValidating)) return
    setWidgetData(parseRewardsAnalyticsWidgetsData(widgetResponse))
  }, [widgetResponse, isValidating])

  return (
    <div
      className={`hover:shadow-sm rounded-lg transition-all duration-200 ease-in-out`}
      style={{
        gridColumnStart: widgetData?.positionX ? widgetData?.positionX + 1 : 1,
        gridTemplateRows: 'auto'
      }}
    >
      <div className='flex flex-col relative h-full gap-6 px-3 py-4 bg-white border rounded border-gray-light'>
        <h2 className='text-base font-medium text-carbon underline underline-offset-4 decoration-2 decoration-coal-lighter decoration-dashed'>
          {widgetData?.title}
        </h2>
        {Boolean(widgetData?.primaryMetric) && widgetData?.primaryMetric !== undefined && (
          <GenericMetricData widgetData={widgetData?.primaryMetric} type='PRIMARY' />
        )}
        {Boolean(widgetData?.secondaryMetricsList) && widgetData?.secondaryMetricsList !== undefined && (
          <div className='grid grid-cols-2 gap-6'>
            {widgetData?.secondaryMetricsList?.map((widgetData: AnalyticsMetricType, index: number) => {
              return <GenericMetricData key={index} widgetData={widgetData} type='SECONDARY' />
            })}
          </div>
        )}
        {Boolean(widgetData?.detailedMetricsList) && widgetData?.detailedMetricsList !== undefined && (
          <div className='flex flex-col'>
            {widgetData?.detailedMetricsList?.map((widgetList: AnalyticsMetricType[], index: number) => {
              return (
                <div key={index} className={`flex flex-col py-4 gap-3 ${index > 0 ? 'border-t-1' : ''}`}>
                  {widgetList?.map((widgetData: AnalyticsMetricType, index: number) => {
                    return <GenericMetricData key={index} widgetData={widgetData} type='LIST' />
                  })}
                </div>
              )
            })}
          </div>
        )}
        <ComponentLoader
          isLoaderVisible={isValidating}
          message='Loading Widget...'
          customClass='border rounded border-gray-light'
          isComponent
        />
      </div>
    </div>
  )
}

export default RewardAnalyticsWidget
